import { useState } from "react";
import { type RestaurantWithEventSpaces } from "@/types/restaurant";
import { getOptimizedImageUrl, getImageSrcSet } from "@/utils/imageOptimization";
import { getNeighborhoodDisplay } from "@/utils/neighborhoodFormatting";

interface RestaurantImageProps {
  restaurant: RestaurantWithEventSpaces;
  onPhotoStatusChange?: (hasPhoto: boolean) => void;
}

export const RestaurantImage = ({ restaurant, onPhotoStatusChange }: RestaurantImageProps) => {
  const [hasError, setHasError] = useState(false);
  const photoUrl = restaurant.photos?.[0];
  const hasPhoto = !hasError && !!photoUrl;
  const optimizedSrc = photoUrl ? getOptimizedImageUrl(photoUrl, 600) : '';
  const srcSet = photoUrl ? getImageSrcSet(photoUrl, [400, 600, 800]) : '';

  const spaceCount = restaurant.eventspaces?.length || 0;
  const maxCapacity = Math.max(0, ...(restaurant.eventspaces?.map(s => parseInt(s.capacity || '0') || 0) || []));

  const handleError = () => {
    setHasError(true);
    onPhotoStatusChange?.(false);
  };

  if (!hasPhoto) {
    return (
      <div className="relative h-28 overflow-hidden bg-gradient-to-br from-primary via-primary/85 to-secondary/60">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{ backgroundImage: 'url(/pattern.png)', backgroundRepeat: 'repeat' }}
        />
        <div className="relative h-full flex flex-col justify-end p-3 text-white">
          <h3 className="font-display text-lg font-semibold leading-tight truncate">{restaurant.name}</h3>
          <p className="text-sm text-white/70 truncate">
            {getNeighborhoodDisplay(restaurant.neighborhood)}
            {spaceCount > 0 && ` · ${spaceCount} Space${spaceCount !== 1 ? 's' : ''}`}
            {maxCapacity > 0 && ` · Up to ${maxCapacity}`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-44 overflow-hidden">
      <img
        src={optimizedSrc}
        srcSet={srcSet || undefined}
        alt={restaurant.name}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={handleError}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
      />
      {spaceCount > 0 && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent p-3">
          <div className="flex items-center justify-between text-white text-sm">
            <span className="font-medium">
              {spaceCount} Event Space{spaceCount !== 1 ? 's' : ''}
            </span>
            {maxCapacity > 0 && <span>Up to {maxCapacity} guests</span>}
          </div>
        </div>
      )}
    </div>
  );
};
